import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';

import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';

import { DiagnosisResponse, GuaranteeApplication, GuaranteeDiagnosisCoverage } from '@lib/core/models/guarantee-application.interface';
import { PrescriptionTypes } from '@lib/core/models/prescription.interface';

import { ProtocolModalComponent } from '@lib/shared-ui/modals/protocol-modal/protocol-modal.component';

import { InfoBadgeColors, InfoBadgeComponent } from '../info-badge/info-badge.component';

export interface DiagnosisComponentConfig {
  showProtocolLink?: boolean;
  showCoverageStatus?: boolean;
}

@Component({
  selector: 'shared-diagnosis',
  standalone: true,
  imports: [InfoBadgeComponent, MatTooltip],
  template: `
    @if (!!diagnosesCoverage()) {
      <div class="flex flex-col items-start">
        @for (diagnosis of diagnosesCoverage(); track $index) {
          <div class="flex flex-col items-start gap-2">
            @if (diagnosis.protocolId && config().showProtocolLink) {
              @if (diagnosis.icd10Code) {
                <span class="flex items-center gap-2">
                  <a
                    class="cursor-pointer text-wrap text-sm text-blue-primary underline"
                    [matTooltip]="'Деталі протоколу'"
                    [matTooltipDisabled]="!diagnosis.isCovered || !diagnosis.protocolId"
                    (click)="openProtocolDialog(diagnosis.protocolId!)">
                    {{ diagnosis.icd10Code }}
                  </a>
                  @if (config().showCoverageStatus) {
                    <shared-info-badge
                      iconType="mat-icon"
                      [withIcon]="true"
                      [icon]="diagnosis.isCovered ? 'check_circle_outline' : 'error_outline'"
                      [color]="diagnosis.isCovered ? colors.Blue : colors.Red"
                      [text]="diagnosis.isCovered ? 'в покритті' : 'не в покритті'"
                      [fontSize]="'text-sm'"></shared-info-badge>
                  }
                </span>
              }
            } @else {
              @if (!diagnosis.protocolId) {
                <span class="flex items-center gap-1 text-sm text-gray-1">
                  {{ diagnosis.icd10Code }}
                  <shared-info-badge [color]="colors.Black" [text]="'ICPC2'" [textColor]="'!text-white'" [fontSize]="'text-xs'" [rounded]="false"></shared-info-badge>
                </span>
              }
            }
          </div>
        } @empty {
          <span class="text-sm text-gray-2">{{ 'Не вказано' }}</span>
        }
      </div>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiagnosisComponent {
  private readonly dialog = inject(MatDialog);

  protected readonly colors = InfoBadgeColors;

  guarantee = input.required<GuaranteeApplication>();
  config = input<DiagnosisComponentConfig>({
    showProtocolLink: true,
    showCoverageStatus: true,
  });

  protected readonly diagnoses = computed<DiagnosisResponse[]>(() => this.guarantee().diagnoses ?? []);
  protected readonly diagnosesCoverage = computed<GuaranteeDiagnosisCoverage[]>(() => {
    return (
      this.diagnoses()?.map(diagnosis => {
        const coverage = this.guarantee().diagnosesCoverage?.find(coverageItem => coverageItem.icd10Code == diagnosis.icd10?.code);

        return {
          icd10Code: diagnosis.icd10 ? `${diagnosis.icd10?.code} ${diagnosis.icd10?.name}` : `${diagnosis.icpc2?.code} ${diagnosis.icpc2?.name}`,
          protocolId: coverage?.protocolId ?? null,
          isCovered: coverage?.isCovered ?? false,
        } as GuaranteeDiagnosisCoverage;
      }) ?? []
    );
  });

  protected readonly prescriptions = computed(() => {
    const prescriptions = this.guarantee().prescriptions ?? [];

    return {
      drugs: prescriptions.filter(item => item.type === PrescriptionTypes.Drugs),
      relatedSpecialists: prescriptions.filter(item => item.type === PrescriptionTypes.RelatedSpecialists),
      laboratoryServices: prescriptions.filter(item => item.type === PrescriptionTypes.LaboratoryService),
      instrumentalDiagnostics: prescriptions.filter(item => item.type === PrescriptionTypes.InstrumentalDiagnostic),
      personalDoctors: prescriptions.filter(item => item.type === PrescriptionTypes.PersonalDoctorAppointment),
    };
  });

  protected openProtocolDialog(protocolId: string) {
    this.dialog.open(ProtocolModalComponent, {
      data: {
        protocolId: protocolId,
        prescriptions: this.prescriptions(),
      },
      autoFocus: '__non_existing_element__',
      width: '800px',
    });
  }
}
